import axios from 'axios';
import {
  ApiResponse,
  Candidate,
  InterviewSession,
  EvaluationData,
  SelectedJobTypesResponse,
} from '../types/models';
import { auth } from '../config/firebase';

// APIのベースURL
const ENV_API_URL = process.env.REACT_APP_API_BASE_URL;
let API_BASE_URL: string;

if (ENV_API_URL) {
  // 環境変数がある場合、/api/v1が含まれているかチェック
  API_BASE_URL = ENV_API_URL.endsWith('/api/v1') ? ENV_API_URL : `${ENV_API_URL}/api/v1`;
} else {
  // 環境変数がない場合のデフォルト値
  API_BASE_URL = 'http://localhost:3001/api/v1';
}

// AI Agent APIは aiInterviewService に移動しました

// APIクライアントの設定
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30秒に延長
  headers: {
    'Content-Type': 'application/json',
  },
});

// 強制的にbaseURLを設定（念のため）
apiClient.defaults.baseURL = API_BASE_URL;

// リクエストインターセプター
apiClient.interceptors.request.use(
  async (config) => {
    // Firebase認証ユーザーがログインしている場合、IDトークンを取得してAuthorizationヘッダーに追加
    const firebaseUser = auth.currentUser;
    const headersRecord =
      (config.headers as unknown as Record<string, unknown> | undefined) ?? undefined;
    const existingAuthorization =
      typeof headersRecord?.Authorization === 'string' ||
      typeof headersRecord?.authorization === 'string';

    // 既にAuthorizationが明示されている場合（例: 候補者トークン）は上書きしない
    if (firebaseUser && !existingAuthorization) {
      try {
        const idToken = await firebaseUser.getIdToken();
        config.headers.Authorization = `Bearer ${idToken}`;
      } catch (error) {
        console.warn('Firebase IDトークンの取得に失敗しました:', error);
        // トークン取得失敗時はそのまま続行（認証が必要なエンドポイントで401が返る）
      }
      // バックエンドでのフォールバック認証用に Firebase UID も送信（開発環境向け）
      config.headers['X-Firebase-Uid'] = firebaseUser.uid;
    }
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// レスポンスインターセプター
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // 401/403エラーの統一ハンドリング
    if (axios.isAxiosError(error) && error.response) {
      const status = error.response.status;

      if (status === 401) {
        // 認証エラー: ログイン画面にリダイレクト（無限リロード対策付き）
        console.warn('認証エラー (401): ログインが必要です');
        const currentPath = window.location.pathname;

        if (currentPath.startsWith('/admin/')) {
          // 企業管理画面の場合:
          // - /admin/[companyId]/... （サブページ）のときだけ企業トップ `/admin/[companyId]` へ戻す
          // - /admin や /admin/[companyId] そのものではリダイレクトしない（無限リロード防止）
          const match = currentPath.match(/^\/admin\/([^/]+)(\/.*)?$/);
          const companyId = match?.[1] ?? null;
          const hasSubPath = Boolean(match?.[2]);

          if (companyId && hasSubPath) {
            window.location.href = `/admin/${companyId}`;
          }
        }
      } else if (status === 403) {
        // 認可エラー: アクセス権限がない
        console.warn('認可エラー (403): このリソースへのアクセス権限がありません');
        // エラーメッセージは各API呼び出し側で処理される
      }
    }

    console.error('Response error:', error);
    return Promise.reject(error);
  }
);

// 企業関連のAPI
export const companyApi = {
  // 企業一覧取得
  getCompanies: async (): Promise<ApiResponse<any[]>> => {
    try {
      const fullUrl = `${API_BASE_URL}/companies`;

      const response = await axios.get<ApiResponse<any[]>>(fullUrl, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000,
      });

      return response.data;
    } catch (error) {
      console.error('❌ API Error:', error);
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any[]>;
      }
      return {
        success: false,
        message: '企業一覧の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 企業詳細取得
  getCompany: async (id: string): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.get<ApiResponse<any>>(`/companies/${id}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: '企業情報の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 企業情報更新
  updateCompany: async (
    id: string,
    updateData: { name?: string; industryId?: string | null }
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.put<ApiResponse<any>>(`/companies/${id}`, updateData);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: '企業情報の更新中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 企業の custom-skills 取得
  getCompanyCustomSkills: async (id: string): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.get<ApiResponse<any>>(`/companies/${id}/custom-skills`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: 'カスタムスキル設定の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 企業の custom-skills 更新
  updateCompanyCustomSkills: async (id: string, settings: any): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.put<ApiResponse<any>>(
        `/companies/${id}/custom-skills`,
        settings
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: 'カスタムスキル設定の更新中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 企業の固定質問取得
  getCompanyFixedQuestions: async (
    id: string
  ): Promise<
    ApiResponse<{
      questions: Array<{ questionNumber: number; question: string; instruction?: string | null }>;
    }>
  > => {
    try {
      const response = await apiClient.get<
        ApiResponse<{
          questions: Array<{
            questionNumber: number;
            question: string;
            instruction?: string | null;
          }>;
        }>
      >(`/companies/${id}/fixed-questions`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<{
          questions: Array<{
            questionNumber: number;
            question: string;
            instruction?: string | null;
          }>;
        }>;
      }
      return {
        success: false,
        message: '固定質問の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 企業の固定質問更新
  updateCompanyFixedQuestions: async (
    id: string,
    questions: Array<{ question: string; instruction?: string }>
  ): Promise<
    ApiResponse<{
      questions: Array<{ questionNumber: number; question: string; instruction?: string | null }>;
    }>
  > => {
    try {
      const response = await apiClient.put<
        ApiResponse<{
          questions: Array<{
            questionNumber: number;
            question: string;
            instruction?: string | null;
          }>;
        }>
      >(`/companies/${id}/fixed-questions`, { questions });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<{
          questions: Array<{
            questionNumber: number;
            question: string;
            instruction?: string | null;
          }>;
        }>;
      }
      return {
        success: false,
        message: '固定質問の更新中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 企業の選択済み職種取得
  getCompanySelectedJobTypes: async (id: string): Promise<SelectedJobTypesResponse> => {
    try {
      const response = await apiClient.get<SelectedJobTypesResponse>(
        `/companies/${id}/selected-job-types`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as SelectedJobTypesResponse;
      }
      return {
        success: false,
        message: '選択済み職種の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 企業の選択済み職種更新
  updateCompanySelectedJobTypes: async (
    id: string,
    jobTypeIds: string[]
  ): Promise<SelectedJobTypesResponse> => {
    try {
      const response = await apiClient.put<SelectedJobTypesResponse>(
        `/companies/${id}/selected-job-types`,
        { jobTypeIds }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as SelectedJobTypesResponse;
      }
      return {
        success: false,
        message: '選択済み職種の更新中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 面接情報取得
  getInterview: async (companyId: string, interviewId: string): Promise<ApiResponse<any>> => {
    try {
      // 面接一覧を取得して該当のIDを検索
      const fullUrl = `${API_BASE_URL}/interviews`;

      const response = await axios.get<ApiResponse<any[]>>(fullUrl, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000,
      });

      if (response.data.success && response.data.data) {
        // 該当する面接を検索
        const interview = response.data.data.find(
          (item: any) => item.id === interviewId && item.companyId === companyId
        );

        if (interview) {
          return {
            success: true,
            data: interview,
            message: '面接情報を取得しました',
          };
        } else {
          return {
            success: false,
            message: `指定された面接が見つかりません (Company: ${companyId}, Interview: ${interviewId})`,
            error: 'Interview not found',
          };
        }
      } else {
        return {
          success: false,
          message: '面接一覧の取得に失敗しました',
          error: response.data.error || '不明なエラー',
        };
      }
    } catch (error) {
      console.error('❌ API Error:', error);
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: '面接情報の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 企業ユーザー招待
  inviteUser: async (companyId: string, inviteEmail: string): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.post<ApiResponse<any>>('/company-users/invite', {
        companyId,
        inviteEmail,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: '招待の送信中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 企業ユーザー一覧取得
  getCompanyUsers: async (companyId: string): Promise<ApiResponse<any[]>> => {
    try {
      const response = await apiClient.get<ApiResponse<{ users: any[]; invitations: any[] }>>(
        `/company-users/${companyId}`
      );
      if (response.data.success && response.data.data) {
        return {
          success: true,
          data: response.data.data.users,
          message: 'ユーザー一覧を取得しました',
        };
      }
      return {
        success: false,
        message: 'データの形式が正しくありません',
        error: 'Invalid data format',
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any[]>;
      }
      return {
        success: false,
        message: '企業ユーザー一覧の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 企業ユーザー招待一覧取得
  getCompanyInvitations: async (companyId: string): Promise<ApiResponse<any[]>> => {
    try {
      const response = await apiClient.get<ApiResponse<{ users: any[]; invitations: any[] }>>(
        `/company-users/${companyId}`
      );
      if (response.data.success && response.data.data) {
        return {
          success: true,
          data: response.data.data.invitations,
          message: '招待一覧を取得しました',
        };
      }
      return {
        success: false,
        message: 'データの形式が正しくありません',
        error: 'Invalid data format',
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any[]>;
      }
      return {
        success: false,
        message: '招待一覧の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 招待のキャンセル
  cancelInvitation: async (invitationId: string): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.patch<ApiResponse<any>>(
        `/company-users/invitations/${invitationId}/cancel`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: '招待のキャンセル中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 招待の再送信
  resendInvitation: async (companyId: string, inviteEmail: string): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.post<ApiResponse<any>>('/company-users/invite', {
        companyId,
        inviteEmail,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: '招待の再送信中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 企業ユーザー削除
  deleteCompanyUser: async (userId: string): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.delete<ApiResponse<any>>(`/company-users/${userId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: 'ユーザー削除中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },
};

// 職種マスタ型
export interface JobType {
  id: string;
  name: string;
}

export interface MonthlyCompletionBucket {
  year: number;
  month: number;
  label: string;
  completedCount: number;
}

export interface MonthlyCompletionData {
  summary: {
    totalCompleted: number;
    period: {
      from: string;
      to: string;
      fromLabel: string;
      toLabel: string;
    };
  };
  monthly: MonthlyCompletionBucket[];
}

export const interviewStatsApi = {
  getMonthlyCompletion: async (params?: {
    from?: string;
    to?: string;
  }): Promise<ApiResponse<MonthlyCompletionData>> => {
    try {
      const response = await apiClient.get<ApiResponse<MonthlyCompletionData>>(
        '/interviews/stats/monthly-completion',
        { params }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<MonthlyCompletionData>;
      }
      return {
        success: false,
        message: '月次受験完了者数の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },
};

// 職種マスタ関連のAPI
export const jobTypeApi = {
  getJobTypes: async (): Promise<ApiResponse<JobType[]>> => {
    try {
      const response = await apiClient.get<ApiResponse<JobType[]>>('/job-types');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<JobType[]>;
      }
      return {
        success: false,
        message: '職種マスタ一覧の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },
};

// 業界マスタ型
export interface Industry {
  id: string;
  name: string;
}

// 業界マスタ関連のAPI
export const industryApi = {
  getIndustries: async (): Promise<ApiResponse<Industry[]>> => {
    try {
      const response = await apiClient.get<ApiResponse<Industry[]>>('/industries');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<Industry[]>;
      }
      return {
        success: false,
        message: '業界マスタ一覧の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },
};

// 応募求人マスタ型
export interface JobPosting {
  id: string;
  companyId: string;
  title: string;
  description?: string | null;
  isActive: boolean;
  sortOrder?: number | null;
  createdAt?: string;
  updatedAt?: string;
}

// 応募求人マスタ関連のAPI
export const jobPostingApi = {
  getJobPostings: async (companyId: string): Promise<ApiResponse<JobPosting[]>> => {
    try {
      const response = await apiClient.get<ApiResponse<JobPosting[]>>(
        `/companies/${companyId}/job-postings`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<JobPosting[]>;
      }
      return {
        success: false,
        message: '応募求人一覧の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },
  createJobPosting: async (
    companyId: string,
    data: {
      title: string;
      description?: string | null;
      isActive?: boolean;
      sortOrder?: number | null;
    }
  ): Promise<ApiResponse<JobPosting>> => {
    try {
      const response = await apiClient.post<ApiResponse<JobPosting>>(
        `/companies/${companyId}/job-postings`,
        data
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<JobPosting>;
      }
      return {
        success: false,
        message: '応募求人の作成中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },
  updateJobPosting: async (
    companyId: string,
    jobPostingId: string,
    data: {
      title?: string;
      description?: string | null;
      isActive?: boolean;
      sortOrder?: number | null;
    }
  ): Promise<ApiResponse<JobPosting>> => {
    try {
      const response = await apiClient.put<ApiResponse<JobPosting>>(
        `/companies/${companyId}/job-postings/${jobPostingId}`,
        data
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<JobPosting>;
      }
      return {
        success: false,
        message: '応募求人の更新中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },
  deleteJobPosting: async (
    companyId: string,
    jobPostingId: string
  ): Promise<ApiResponse<{ deleted: boolean; wasInUse: boolean; usageCount: number } | null>> => {
    try {
      const response = await apiClient.delete<
        ApiResponse<{ deleted: boolean; wasInUse: boolean; usageCount: number } | null>
      >(`/companies/${companyId}/job-postings/${jobPostingId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<{
          deleted: boolean;
          wasInUse: boolean;
          usageCount: number;
        } | null>;
      }
      return {
        success: false,
        message: '応募求人の削除中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },
  importJobPostings: async (
    companyId: string,
    file: File
  ): Promise<ApiResponse<{ imported: number; errors: string[] }>> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post<ApiResponse<{ imported: number; errors: string[] }>>(
        `/companies/${companyId}/job-postings/import`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<{ imported: number; errors: string[] }>;
      }
      return {
        success: false,
        message: 'CSVインポート中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },
  downloadJobPostingTemplate: async (companyId: string): Promise<void> => {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('認証が必要です');
      }

      const idToken = await firebaseUser.getIdToken();
      const response = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/job-postings/template`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'X-Firebase-Uid': firebaseUser.uid,
          },
          responseType: 'blob',
        }
      );

      // Blobからダウンロードリンクを作成
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'job_postings_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('CSVテンプレートダウンロードエラー:', error);
      throw error;
    }
  },
};

// 候補者関連のAPI
export const candidateApi = {
  // 候補者の作成
  createCandidate: async (candidateData: {
    name: string;
    email: string;
    phone?: string;
    position?: string;
    jobTypeId?: string;
    jobTypeLabel?: string;
    candidateType?: 'chuuto' | 'shinsotsu';
    companyId?: string;
    assigneeUserIds?: string[];
  }): Promise<ApiResponse<Candidate>> => {
    try {
      const response = await apiClient.post<ApiResponse<Candidate>>('/candidates', candidateData);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<Candidate>;
      }
      return {
        success: false,
        message: '候補者の作成中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 候補者の一覧取得（企業IDフィルタリング対応）
  getCandidates: async (companyId?: string): Promise<ApiResponse<Candidate[]>> => {
    try {
      const response = await apiClient.get<ApiResponse<Candidate[]>>('/candidates', {
        params: companyId ? { companyId } : undefined,
      });

      return response.data;
    } catch (error) {
      console.error('❌ API Error:', error);
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<Candidate[]>;
      }
      return {
        success: false,
        message: '候補者一覧の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 候補者の詳細取得
  getCandidate: async (id: string): Promise<ApiResponse<Candidate>> => {
    try {
      const response = await apiClient.get<ApiResponse<Candidate>>(`/candidates/${id}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<Candidate>;
      }
      return {
        success: false,
        message: '候補者情報の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 候補者情報の更新
  updateCandidate: async (
    id: string,
    candidateData: {
      name?: string;
      email?: string;
      phone?: string;
      position?: string;
      jobTypeId?: string;
      jobTypeLabel?: string;
      candidateType?: 'chuuto' | 'shinsotsu';
      assigneeUserIds?: string[];
    }
  ): Promise<ApiResponse<Candidate>> => {
    try {
      const response = await apiClient.put<ApiResponse<Candidate>>(
        `/candidates/${id}`,
        candidateData
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<Candidate>;
      }
      return {
        success: false,
        message: '候補者情報の更新中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 候補者の削除
  deleteCandidate: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    try {
      const response = await apiClient.delete<ApiResponse<{ message: string }>>(
        `/candidates/${id}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<{ message: string }>;
      }
      return {
        success: false,
        message: '候補者の削除中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 候補者の面接情報付き詳細取得
  getCandidateWithInterviews: async (
    id: string
  ): Promise<
    ApiResponse<
      Candidate & {
        interviews: Array<{
          id: string;
          date: string;
          startTime: string;
          endTime: string;
          status: string;
          type: string;
          interviewUrl: string;
          evaluation?: {
            id?: string;
            totalScore?: number;
            rank?: 'excellent' | 'good' | 'average' | 'below_standard';
            recommendation?: 'strong_hire' | 'hire' | 'additional_review' | 'no_hire';
            summary?: string;
            evaluationText?: string;
            careerSummary?: string;
            evaluatedAt?: string;
            detailedEvaluation?: {
              specializedSkills?: {
                skill_1?: { score: number; maxScore: number; title?: string; evaluation?: string };
                skill_2?: { score: number; maxScore: number; title?: string; evaluation?: string };
                skill_3?: { score: number; maxScore: number; title?: string; evaluation?: string };
                total?: number;
              };
              genericSkills?: {
                skill_1?: { score: number; maxScore: number; title?: string; evaluation?: string };
                skill_2?: { score: number; maxScore: number; title?: string; evaluation?: string };
                skill_3?: { score: number; maxScore: number; title?: string; evaluation?: string };
                total?: number;
              };
              customSkills?: {
                skill_1?: { score: number; maxScore: number; title?: string; evaluation?: string };
                skill_2?: { score: number; maxScore: number; title?: string; evaluation?: string };
                skill_3?: { score: number; maxScore: number; title?: string; evaluation?: string };
                total?: number;
              };
            };
            storage?: {
              savedToDrive?: boolean;
              googleDriveJsonFileId?: string;
              filePaths?: string[];
            };
            version?: string;
          };
        }>;
        evaluation?: {
          totalScore?: number;
          rank?: 'excellent' | 'good' | 'average' | 'below_standard';
          recommendation?: 'strong_hire' | 'hire' | 'additional_review' | 'no_hire';
          summary?: string;
          evaluationText?: string;
          careerSummary?: string;
          evaluatedAt?: string;
          detailedEvaluation?: {
            specializedSkills?: {
              skill_1?: { score: number; maxScore: number; title?: string; evaluation?: string };
              skill_2?: { score: number; maxScore: number; title?: string; evaluation?: string };
              skill_3?: { score: number; maxScore: number; title?: string; evaluation?: string };
              total?: number;
            };
            genericSkills?: {
              skill_1?: { score: number; maxScore: number; title?: string; evaluation?: string };
              skill_2?: { score: number; maxScore: number; title?: string; evaluation?: string };
              skill_3?: { score: number; maxScore: number; title?: string; evaluation?: string };
              total?: number;
            };
            customSkills?: {
              skill_1?: { score: number; maxScore: number; title?: string; evaluation?: string };
              skill_2?: { score: number; maxScore: number; title?: string; evaluation?: string };
              skill_3?: { score: number; maxScore: number; title?: string; evaluation?: string };
              total?: number;
            };
          };
          storage?: {
            savedToDrive?: boolean;
            googleDriveJsonFileId?: string;
            filePaths?: string[];
          };
          version?: string;
        };
      }
    >
  > => {
    try {
      const response = await apiClient.get<ApiResponse<any>>(`/candidates/${id}/with-interviews`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: '候補者詳細（面接情報付き）の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },
};

// 面接スケジュール関連のAPI
export const interviewApi = {
  // 面接スケジュール一覧の取得（企業IDフィルタリング対応）
  getInterviews: async (companyId?: string): Promise<ApiResponse<InterviewSession[]>> => {
    try {
      const response = await apiClient.get<ApiResponse<InterviewSession[]>>('/interviews', {
        params: companyId ? { companyId } : undefined,
      });

      return response.data;
    } catch (error) {
      console.error('❌ API Error:', error);
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<InterviewSession[]>;
      }
      return {
        success: false,
        message: '面接一覧の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 面接スケジュールの作成
  createInterview: async (interviewData: {
    candidateId: string;
    candidateName: string;
    candidateEmail?: string;
    position?: string;
    date: string;
    endDate?: string;
    time: string;
    duration?: number;
    type?: string;
    interviewType?: string;
    companyId?: string;
    jobPostingId?: string | null;
  }): Promise<ApiResponse<InterviewSession>> => {
    try {
      const response = await apiClient.post<ApiResponse<InterviewSession>>(
        '/interviews',
        interviewData
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<InterviewSession>;
      }
      return {
        success: false,
        message: '面接スケジュールの作成中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 面接スケジュールの更新
  updateInterview: async (
    id: string,
    interviewData: {
      candidateName?: string;
      candidateEmail?: string;
      position?: string;
      date?: string;
      time?: string;
      duration?: number;
      type?: string;
      interviewType?: string;
      status?: string;
    }
  ): Promise<ApiResponse<InterviewSession>> => {
    try {
      const response = await apiClient.put<ApiResponse<InterviewSession>>(
        `/interviews/${id}`,
        interviewData
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<InterviewSession>;
      }
      return {
        success: false,
        message: '面接スケジュールの更新中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 面接スケジュールの削除
  deleteInterview: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    try {
      const response = await apiClient.delete<ApiResponse<{ message: string }>>(
        `/interviews/${id}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<{ message: string }>;
      }
      return {
        success: false,
        message: '面接スケジュールの削除中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 面接セッションの作成（既存API）
  createSession: async (sessionData: {
    candidateId: string;
    candidateName: string;
    companyId?: string;
  }): Promise<ApiResponse<InterviewSession>> => {
    try {
      const response = await apiClient.post<ApiResponse<InterviewSession>>(
        '/interviews/sessions',
        sessionData
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<InterviewSession>;
      }
      return {
        success: false,
        message: '面接セッションの作成中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 面接セッション一覧の取得（既存API）
  getSessions: async (): Promise<ApiResponse<InterviewSession[]>> => {
    try {
      const response = await apiClient.get<ApiResponse<InterviewSession[]>>('/interviews/sessions');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<InterviewSession[]>;
      }
      return {
        success: false,
        message: '面接セッション一覧の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 面接セッションの詳細取得（既存API）
  getSession: async (id: string): Promise<ApiResponse<InterviewSession>> => {
    try {
      const response = await apiClient.get<ApiResponse<InterviewSession>>(
        `/interviews/sessions/${id}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<InterviewSession>;
      }
      return {
        success: false,
        message: '面接セッション情報の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 面接評価の取得
  getEvaluation: async (sessionId: number): Promise<ApiResponse<EvaluationData>> => {
    try {
      const response = await apiClient.get<ApiResponse<EvaluationData>>(
        `/interviews/evaluations/${sessionId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<EvaluationData>;
      }
      return {
        success: false,
        message: '評価データの取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 面接ステータス取得（トークン対応）
  getInterviewStatus: async (
    interviewId: string,
    candidateToken?: string
  ): Promise<ApiResponse<{ status: string; startTime?: string; endTime?: string }>> => {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (candidateToken) {
        headers['Authorization'] = `Bearer ${candidateToken}`;
      }

      const response = await apiClient.get<
        ApiResponse<{ status: string; startTime?: string; endTime?: string }>
      >(`/interviews/${interviewId}/status`, { headers });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<{
          status: string;
          startTime?: string;
          endTime?: string;
        }>;
      }
      return {
        success: false,
        message: '面接ステータスの取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // トークンから面接セッション情報を取得
  getInterviewByToken: async (candidateToken: string): Promise<ApiResponse<any>> => {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${candidateToken}`,
      };

      const response = await apiClient.get<ApiResponse<any>>('/interviews/by-token', {
        headers,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: '面接情報の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 面接開始（トークン対応）
  // 「最初からやり直す」用: 会話ログを初期化してフェーズを導入に戻す
  resetInterviewConversation: async (
    interviewId: string,
    candidateToken?: string
  ): Promise<ApiResponse<{ interviewId: string }>> => {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (candidateToken) {
        headers['Authorization'] = `Bearer ${candidateToken}`;
      }

      const response = await apiClient.post<ApiResponse<{ interviewId: string }>>(
        `/interviews/${interviewId}/reset-conversation`,
        {},
        { headers }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<{ interviewId: string }>;
      }
      return {
        success: false,
        message: '会話ログの初期化中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  startInterview: async (
    interviewId: string,
    candidateToken?: string,
    startTime?: string
  ): Promise<ApiResponse<{ message: string }>> => {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (candidateToken) {
        headers['Authorization'] = `Bearer ${candidateToken}`;
      }

      const response = await apiClient.post<ApiResponse<{ message: string }>>(
        `/interviews/${interviewId}/start`,
        startTime ? { startTime } : {},
        { headers }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<{ message: string }>;
      }
      return {
        success: false,
        message: '面接の開始中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 面接終了
  endInterview: async (sessionId: number): Promise<ApiResponse<{ message: string }>> => {
    try {
      const response = await apiClient.post<ApiResponse<{ message: string }>>(
        `/interviews/sessions/${sessionId}/end`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<{ message: string }>;
      }
      return {
        success: false,
        message: '面接の終了中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 面接完了（評価あり、トークン対応）
  completeInterview: async (
    interviewId: string,
    data: {
      conversationLog: any[];
      evaluationData?: any;
      interviewDuration: number;
      endTime: string;
      googleDriveFolderPath?: string | null;
    },
    candidateToken?: string
  ): Promise<ApiResponse<any>> => {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (candidateToken) {
        headers['Authorization'] = `Bearer ${candidateToken}`;
      }

      const response = await apiClient.post<ApiResponse<any>>(
        `/interviews/${interviewId}/complete`,
        data,
        { headers }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: '面接完了処理中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 面接キャンセル（評価なし、トークン対応）
  cancelInterview: async (
    interviewId: string,
    data: {
      reason: string;
      conversationLog: any[];
      endTime: string;
    },
    candidateToken?: string
  ): Promise<ApiResponse<any>> => {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (candidateToken) {
        headers['Authorization'] = `Bearer ${candidateToken}`;
      }

      const response = await apiClient.post<ApiResponse<any>>(
        `/interviews/${interviewId}/cancel`,
        data,
        { headers }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: '面接キャンセル処理中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  getResumeData: async (
    interviewId: string,
    candidateToken?: string
  ): Promise<
    ApiResponse<{
      interviewId: string;
      interviewStatus: string;
      currentPhase: string;
      messages: Array<{ speaker: string; message: string }>;
      updatedAt: string;
      candidateName?: string;
    }>
  > => {
    try {
      const headers: Record<string, string> = {};
      if (candidateToken) headers['Authorization'] = `Bearer ${candidateToken}`;
      const response = await apiClient.get<ApiResponse<any>>(
        `/interviews/${interviewId}/resume-data`,
        { headers }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response)
        return error.response.data as ApiResponse<any>;
      return {
        success: false,
        message: '再開データ取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  advancePhase: async (
    interviewId: string,
    data: { nextPhase: 'INTRO' | 'CAREER' | 'CASE' | 'CLOSING'; conversationLog?: any },
    candidateToken?: string
  ): Promise<ApiResponse<{ currentPhase: string }>> => {
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (candidateToken) headers['Authorization'] = `Bearer ${candidateToken}`;
      const response = await apiClient.post<ApiResponse<{ currentPhase: string }>>(
        `/interviews/${interviewId}/advance-phase`,
        data,
        { headers }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: 'フェーズ更新中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },
};

// 経歴サマリー関連のAPI
export const careerSummaryApi = {
  // 経歴サマリー取得
  getCareerSummary: async (
    candidateId: string
  ): Promise<ApiResponse<{ careerSummary: string }>> => {
    try {
      const response = await apiClient.get<ApiResponse<{ careerSummary: string }>>(
        `/career-summaries/${candidateId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<{ careerSummary: string }>;
      }
      return {
        success: false,
        message: '経歴サマリーの取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 経歴サマリー作成・更新
  updateCareerSummary: async (
    candidateId: string,
    careerSummary: string
  ): Promise<ApiResponse<{ careerSummary: string }>> => {
    try {
      const response = await apiClient.put<ApiResponse<{ careerSummary: string }>>(
        `/career-summaries/${candidateId}`,
        {
          careerSummary,
        }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<{ careerSummary: string }>;
      }
      return {
        success: false,
        message: '経歴サマリーの更新中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 経歴サマリー削除
  deleteCareerSummary: async (candidateId: string): Promise<ApiResponse<{ message: string }>> => {
    try {
      const response = await apiClient.delete<ApiResponse<{ message: string }>>(
        `/career-summaries/${candidateId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<{ message: string }>;
      }
      return {
        success: false,
        message: '経歴サマリーの削除中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },
};

// Text-to-Speech関連のAPI
export const ttsApi = {
  // テキストから音声を生成
  synthesizeSpeech: async (
    text: string,
    voiceName?: string,
    speakingRate?: number
  ): Promise<Blob> => {
    try {
      const response = await apiClient.post(
        '/tts/synthesize',
        {
          text,
          languageCode: 'ja-JP',
          voiceName: voiceName || 'ja-JP-Chirp3-HD-Umbriel',
          speakingRate: speakingRate ?? 1.0,
        },
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 60000, // TTSリクエストは60秒に延長
        }
      );
      return response.data;
    } catch (error) {
      console.error('TTS synthesis error:', error);
      if (axios.isAxiosError(error)) {
        console.error('TTS API Error Details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          headers: error.response?.headers,
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
          requestData: error.config?.data,
        });
      }
      throw error;
    }
  },

  // 利用可能な日本語音声の一覧を取得
  getVoices: async (): Promise<ApiResponse<{ voices: any[] }>> => {
    try {
      const response = await apiClient.get<ApiResponse<{ voices: any[] }>>('/tts/voices');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<{ voices: any[] }>;
      }
      return {
        success: false,
        message: '音声一覧の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },
};

// プロンプトテンプレート関連のAPI
export const interviewPromptApi = {
  // プロンプトテンプレート一覧取得
  getPromptTemplates: async (companyId: string): Promise<ApiResponse<any[]>> => {
    try {
      const response = await apiClient.get<ApiResponse<any[]>>(
        `/companies/${companyId}/interview-prompts`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any[]>;
      }
      return {
        success: false,
        message: 'プロンプトテンプレート一覧の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // プロンプトテンプレート詳細取得
  getPromptTemplate: async (companyId: string, promptId: string): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.get<ApiResponse<any>>(
        `/companies/${companyId}/interview-prompts/${promptId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: 'プロンプトテンプレート詳細の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // プロンプトテンプレート作成
  createPromptTemplate: async (
    companyId: string,
    promptData: {
      name: string;
      description?: string;
      prompt: string;
      variables?: string[];
      orderIndex: number;
      promptType: 'OPENING' | 'MAIN' | 'CLOSING' | 'FOLLOWUP';
      conditions?: Record<string, any>;
      isActive?: boolean;
    }
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.post<ApiResponse<any>>(
        `/companies/${companyId}/interview-prompts`,
        promptData
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: 'プロンプトテンプレートの作成中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // プロンプトテンプレート更新
  updatePromptTemplate: async (
    companyId: string,
    promptId: string,
    promptData: {
      name?: string;
      description?: string;
      prompt?: string;
      variables?: string[];
      orderIndex?: number;
      promptType?: 'OPENING' | 'MAIN' | 'CLOSING' | 'FOLLOWUP';
      conditions?: Record<string, any>;
      isActive?: boolean;
    }
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.put<ApiResponse<any>>(
        `/companies/${companyId}/interview-prompts/${promptId}`,
        promptData
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: 'プロンプトテンプレートの更新中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // プロンプトテンプレート削除
  deletePromptTemplate: async (companyId: string, promptId: string): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.delete<ApiResponse<any>>(
        `/companies/${companyId}/interview-prompts/${promptId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: 'プロンプトテンプレートの削除中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // プロンプト順序更新
  updatePromptOrder: async (
    companyId: string,
    promptOrders: { id: string; orderIndex: number }[]
  ): Promise<ApiResponse<any[]>> => {
    try {
      const response = await apiClient.put<ApiResponse<any[]>>(
        `/companies/${companyId}/interview-prompts/order`,
        {
          promptOrders,
        }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any[]>;
      }
      return {
        success: false,
        message: 'プロンプト順序の更新中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },
};

// プロンプトプリセット関連のAPI
export const promptPresetApi = {
  // プリセットカテゴリ一覧取得
  getPresetCategories: async (): Promise<ApiResponse<Record<string, any[]>>> => {
    try {
      const response = await apiClient.get<ApiResponse<Record<string, any[]>>>(
        '/prompt-presets/preset-categories'
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<Record<string, any[]>>;
      }
      return {
        success: false,
        message: 'プリセットカテゴリ一覧の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // プリセット詳細取得
  getPresetDetail: async (presetId: string): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.get<ApiResponse<any>>(`/prompt-presets/presets/${presetId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: 'プリセット詳細の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 企業の選択済みプリセット一覧取得
  getSelectedPresets: async (companyId: string): Promise<ApiResponse<any[]>> => {
    try {
      const response = await apiClient.get<ApiResponse<any[]>>(
        `/companies/${companyId}/selected-presets`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any[]>;
      }
      return {
        success: false,
        message: '選択済みプリセット一覧の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // プリセット選択/更新
  selectPreset: async (
    companyId: string,
    selectionData: {
      presetId: string;
      isActive?: boolean;
      customizations?: Record<string, any>;
      orderPreference?: number;
    }
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.post<ApiResponse<any>>(
        `/companies/${companyId}/select-preset`,
        selectionData
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: 'プリセット選択の更新中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // プリセット選択解除
  unselectPreset: async (companyId: string, selectionId: string): Promise<ApiResponse<any>> => {
    try {
      const response = await apiClient.delete<ApiResponse<any>>(
        `/companies/${companyId}/selected-presets/${selectionId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: 'プリセット選択解除中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 企業の実行用プロンプト一覧取得
  getActivePrompts: async (companyId: string): Promise<ApiResponse<any[]>> => {
    try {
      const response = await apiClient.get<ApiResponse<any[]>>(
        `/companies/${companyId}/active-prompts`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any[]>;
      }
      return {
        success: false,
        message: '実行用プロンプト一覧の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },
};

// AI面接官関連のAPI（管理画面での選択UIは削除済み、運用側でのAPI/DB操作は可能）
export const aiInterviewerApi = {
  // 利用可能なAI面接官一覧取得
  getInterviewers: async (): Promise<
    ApiResponse<
      Array<{
        id: string;
        name: string;
        title: string;
        description: string;
        icon: string;
        isActive: boolean;
      }>
    >
  > => {
    try {
      const response = await apiClient.get<
        Array<{
          id: string;
          name: string;
          title: string;
          description: string;
          icon: string;
          isActive: boolean;
        }>
      >('/ai-interviewers/interviewers');

      return {
        success: true,
        data: response.data,
        message: 'AI面接官一覧を取得しました',
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: 'AI面接官一覧の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 企業の選択済み面接官取得
  getSelectedInterviewer: async (
    companyId: string
  ): Promise<
    ApiResponse<{
      id: string | null;
      companyId: string;
      interviewerId: string;
      selectedAt: string | null;
      isDefault: boolean;
      interviewer: {
        id: string;
        name: string;
        title: string;
        description: string;
        icon: string;
        isActive: boolean;
      };
    }>
  > => {
    try {
      const response = await apiClient.get<
        ApiResponse<{
          id: string | null;
          companyId: string;
          interviewerId: string;
          selectedAt: string | null;
          isDefault: boolean;
          interviewer: {
            id: string;
            name: string;
            title: string;
            description: string;
            icon: string;
            isActive: boolean;
          };
        }>
      >(`/companies/${companyId}/selected-interviewer`);

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || '選択済み面接官を取得しました',
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: '選択済み面接官の取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 企業の面接官選択（運用側での使用）
  selectInterviewer: async (
    companyId: string,
    interviewerId: string
  ): Promise<
    ApiResponse<{
      id: string;
      companyId: string;
      interviewerId: string;
      selectedAt: string;
      isDefault: boolean;
      interviewer: {
        id: string;
        name: string;
        title: string;
        description: string;
        icon: string;
        isActive: boolean;
      };
    }>
  > => {
    try {
      const response = await apiClient.post<
        ApiResponse<{
          id: string;
          companyId: string;
          interviewerId: string;
          selectedAt: string;
          isDefault: boolean;
          interviewer: {
            id: string;
            name: string;
            title: string;
            description: string;
            icon: string;
            isActive: boolean;
          };
        }>
      >(`/companies/${companyId}/select-interviewer`, { interviewerId });

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'AI面接官を選択しました',
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: 'AI面接官の選択中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },

  // 企業の実行用システムプロンプト取得
  getActivePrompt: async (
    companyId: string
  ): Promise<
    ApiResponse<{
      interviewerId: string;
      interviewerName: string;
      interviewerTitle: string;
      systemPrompt: string;
      selectedAt: string | null;
      isDefault: boolean;
    }>
  > => {
    try {
      const response = await apiClient.get<
        ApiResponse<{
          interviewerId: string;
          interviewerName: string;
          interviewerTitle: string;
          systemPrompt: string;
          selectedAt: string | null;
          isDefault: boolean;
        }>
      >(`/companies/${companyId}/active-prompt`);

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || '実行用プロンプトを取得しました',
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<any>;
      }
      return {
        success: false,
        message: '実行用プロンプトの取得中にエラーが発生しました',
        error: error instanceof Error ? error.message : '不明なエラー',
      };
    }
  },
};

// AI面接エージェント関連のAPIは aiInterviewService に移動しました
// 後方互換性のために aiInterviewApi をインポートして再エクスポート
export { aiInterviewApi } from './aiInterviewService';

export default apiClient;
